import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PresentationUtilsService } from 'src/app/presentations/services/presentation-utils.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import {
  StorageFile,
  StorageUtilsService,
} from 'src/app/storage/services/storage-utils.service';
import { StorageService } from 'src/app/storage/services/storage.service';
import { UploadManagerService } from 'src/app/storage/services/upload-manager.service';
import { FileExistenceCheckService } from 'src/app/template-editor/template-components/services/file-existence-check.service';
import { FileMetadataUtilsService } from 'src/app/template-editor/template-components/services/file-metadata-utils.service';
import { LogoImageService } from 'src/app/template-editor/template-components/services/logo-image.service';

export interface BrandLogo {
  file: string;
  thumbnailUrl: string;
}

/**
 * Holds the logo being edited on the Branding page. Changes stay local to the
 * page; nothing is written to the company settings from here.
 */
@Injectable()
export class BrandLogoService {
  private companyStateService = inject(CompanyStateService);
  private broadcaster = inject(BroadcasterService);
  private storageService = inject(StorageService);
  private storageManagerService = inject(StorageManagerService);
  private uploadManagerService = inject(UploadManagerService);
  private presentationUtils = inject(PresentationUtilsService);
  private fileExistenceCheckService = inject(FileExistenceCheckService);
  private fileMetadataUtilsService = inject(FileMetadataUtilsService);

  readonly logo = signal<BrandLogo | null>(null);
  readonly isUploading = signal(false);
  readonly uploadFailed = signal(false);

  readonly acceptedTypes = this.presentationUtils.isMobileBrowser()
    ? 'image/*'
    : this.storageManagerService.getValidExtensions(
        StorageManagerService.FILE_TYPE.IMAGE,
      );

  private readonly uploader = this.uploadManagerService.getFileUploader({
    validType: StorageManagerService.FILE_TYPE.IMAGE,
    onUploadStatus: (isUploading: boolean) => this.isUploading.set(isUploading),
    addFile: (file: StorageFile) => this.setLogoFromStorageFile(file),
    onCompleteItem: (item) => {
      if (!item.isSuccess && !item.isCancel) {
        this.uploadFailed.set(true);
      }
    },
    getFolderPath: () => '',
    detectChanges: () => {},
  });

  constructor() {
    const subscription = this.broadcaster.on(
      'risevision.company.selectedCompanyChanged',
      () => this.load(),
    );
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());

    this.load();
  }

  load(): void {
    const settings =
      this.companyStateService.getCopyOfSelectedCompany()?.settings || {};
    const file: string = settings.brandingLogoFile;

    this.uploadFailed.set(false);

    if (!file) {
      this.logo.set(null);
      return;
    }

    this.logo.set({
      file,
      thumbnailUrl: LogoImageService.DEFAULT_IMAGE_THUMBNAIL,
    });

    this.fileExistenceCheckService
      .requestMetadataFor([file], LogoImageService.DEFAULT_IMAGE_THUMBNAIL)
      .then((metadata) => {
        const thumbnailUrl = metadata?.[0]?.['thumbnail-url'];

        if (thumbnailUrl && this.logo()?.file === file) {
          this.logo.set({ file, thumbnailUrl });
        }
      })
      .catch((error) => {
        console.error('Could not load metadata for: ' + file, error);
      });
  }

  upload(files: File[]): Promise<void> {
    if (!files?.length || this.isUploading()) {
      return Promise.resolve();
    }

    this.uploadFailed.set(false);

    return this.uploader.uploadSelectedFiles([files[0]]);
  }

  selectFromStorage(): Promise<void> {
    if (this.isUploading()) {
      return Promise.resolve();
    }

    return this.storageService
      .showStorageModal(
        StorageUtilsService.SELECTOR_TYPES.SINGLE_FILE,
        StorageUtilsService.SELECTOR_FILTERS.IMAGES,
      )
      .then((files: StorageFile[]) => {
        if (Array.isArray(files) && files.length) {
          this.uploadFailed.set(false);
          this.setLogoFromStorageFile(files[0]);
        }
      })
      .catch(() => {});
  }

  remove(): void {
    this.uploadFailed.set(false);
    this.logo.set(null);
  }

  private setLogoFromStorageFile(file: StorageFile): void {
    const [item] = this.fileMetadataUtilsService.metadataWithFile(
      [],
      LogoImageService.DEFAULT_IMAGE_THUMBNAIL,
      [file],
    );

    this.logo.set({
      file: item.file,
      thumbnailUrl: item['thumbnail-url'],
    });
  }
}
